import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Highlighter, Loader2, RotateCcw, Save, Trash2 } from 'lucide-react';
import { collectDocumentFields, mirrorDocumentField, setDocumentEditHighlights } from '@/lib/documentEditor';
import { deleteCheque, downloadCheque, getChequeRenderHtml, resetChequeEdits, saveCheque, updateChequeStatus } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';

function cleanError(err, fallback) {
  return err?.response?.data?.detail || fallback;
}

const STATUS_STYLES = {
  draft: 'border-slate-200 bg-slate-100 text-slate-700',
  issued: 'border-blue-200 bg-blue-50 text-blue-700',
  cleared: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  void: 'border-red-200 bg-red-50 text-red-700',
};

const NEXT_STATUSES = { draft: ['issued', 'void'], issued: ['cleared', 'void'], cleared: [], void: [] };
const LOCKED_STATUSES = ['cleared', 'void'];

export default function ChequeEditorDialog({ cheque, onClose, onSaved, onDeleted }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(Boolean(cheque));
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [highlights, setHighlights] = useState(true);
  const [chequeDoc, setChequeDoc] = useState(cheque);
  const [statusSaving, setStatusSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { profile } = useAuth();
  const canDelete = profile?.is_super_admin === true || (profile?.role === 'admin' && chequeDoc?.cheque_type === 'commission');
  const frameRef = useRef(null);
  const loadedIdRef = useRef('');
  const currentStatus = chequeDoc?.status || 'draft';
  const editable = !LOCKED_STATUSES.includes(currentStatus);

  useEffect(() => {
    if (!cheque?.id || loadedIdRef.current === cheque.id) return;
    loadedIdRef.current = cheque.id;
    setLoading(true);
    getChequeRenderHtml(cheque.id)
      .then((response) => { setHtml(response.data?.html || ''); setChequeDoc(response.data?.document || cheque); })
      .catch((err) => { toast.error(cleanError(err, 'Cheque editor could not be loaded.')); onClose(); })
      .finally(() => setLoading(false));
  }, [cheque, onClose]);

  const handleFrameLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (!editable) {
      setDocumentEditHighlights(doc, false);
      doc?.querySelectorAll('[contenteditable]').forEach((node) => node.setAttribute('contenteditable', 'false'));
      return;
    }
    setDocumentEditHighlights(doc, highlights);
    doc?.querySelector('[data-document-editor]')?.addEventListener('input', (event) => mirrorDocumentField(doc, event.target));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveCheque(cheque.id, collectDocumentFields(frameRef.current?.contentDocument));
      toast.success('Cheque changes saved.');
      await onSaved?.();
      onClose();
    } catch (err) {
      toast.error(cleanError(err, 'Cheque could not be saved.'));
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Drop the hand edits on this cheque and read it straight from its payments again?')) return;
    try {
      setResetting(true);
      const response = await resetChequeEdits(cheque.id);
      setHtml(response.data?.html || '');
      setChequeDoc(response.data?.document || chequeDoc);
      const cleared = response.data?.cleared || 0;
      toast.success(cleared ? `${cleared} hand edit${cleared === 1 ? '' : 's'} cleared.` : 'This cheque had no hand edits.');
      await onSaved?.();
    } catch (err) {
      toast.error(cleanError(err, 'Cheque edits could not be reset.'));
    } finally { setResetting(false); }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await downloadCheque(cheque.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `MCC_${cheque.cheque_type || 'cheque'}_${cheque.payee_name || 'payment'}.pdf`.replace(/[^a-zA-Z0-9_.-]/g, '_');
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(cleanError(err, 'Cheque could not be downloaded.')); }
    finally { setDownloading(false); }
  };

  const handleStatus = async (status) => {
    const reason = status === 'void' ? window.prompt('Why is this cheque being voided?') : '';
    if (status === 'void' && !reason?.trim()) return;
    const label = status.charAt(0).toUpperCase() + status.slice(1);
    if (!window.confirm(`Mark this cheque as ${label}? This status change cannot be reversed.`)) return;
    try {
      setStatusSaving(true);
      const response = await updateChequeStatus(cheque.id, status, reason || '');
      setChequeDoc(response.data);
      toast.success(`Cheque marked ${label}.`);
      await onSaved?.();
    } catch (err) { toast.error(cleanError(err, 'Cheque status could not be updated.')); }
    finally { setStatusSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(chequeDoc?.cheque_type === 'commission' ? 'Delete this cheque and its commission payments? The invoice amounts will become available again.' : 'Permanently delete this cheque? The payment will remain and a new cheque can be created.')) return;
    try {
      setDeleting(true);
      await deleteCheque(cheque.id);
      toast.success('Cheque deleted.');
      await onDeleted?.();
      onClose();
    } catch (err) { toast.error(cleanError(err, 'Cheque could not be deleted.')); }
    finally { setDeleting(false); }
  };

  const nextStatuses = NEXT_STATUSES[currentStatus] || [];

  return <Dialog open={Boolean(cheque)} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="flex h-[94vh] max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-4 sm:max-w-[calc(100vw-3rem)] sm:p-6">
      <DialogHeader><DialogTitle className="flex items-center gap-2">Cheque Editor <Badge variant="outline" className={STATUS_STYLES[currentStatus]}>{currentStatus.toUpperCase()}</Badge></DialogTitle></DialogHeader>
      {loading ? <div className="flex flex-1 items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading cheque...</div> : <>
        {!editable && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${currentStatus === 'void' ? 'border-red-200 bg-red-50 text-red-800' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
            <p className="font-semibold">{currentStatus === 'void' ? 'Void reason' : 'Cleared - read only'}</p>
            <p className="mt-1 whitespace-pre-wrap">{currentStatus === 'void' ? (chequeDoc?.void_reason || 'No reason recorded') : 'This cheque has cleared the bank and can no longer be edited.'}</p>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2"><div className="flex gap-2">{nextStatuses.map((status) => <Button key={status} variant="outline" size="sm" disabled={statusSaving} className={status === 'void' ? 'border-red-200 text-red-700 hover:bg-red-50' : ''} onClick={() => handleStatus(status)}>Mark {status.charAt(0).toUpperCase() + status.slice(1)}</Button>)}</div>{editable && <div className="flex gap-2"><Button variant="outline" size="sm" onClick={handleReset} disabled={resetting}>{resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}Reset edits</Button><Button variant="outline" size="sm" onClick={() => setHighlights((value) => { setDocumentEditHighlights(frameRef.current?.contentDocument, !value); return !value; })}><Highlighter className="mr-2 h-4 w-4" /> Highlights: {highlights ? 'on' : 'off'}</Button></div>}</div>
        <div className="min-h-0 flex-1 overflow-auto rounded-lg bg-[#5c5f66]"><iframe ref={frameRef} title="Cheque editor" srcDoc={html} sandbox="allow-same-origin" onLoad={handleFrameLoad} className="block h-[1150px] w-full border-0" /></div>
      </>}
      <DialogFooter className="sm:justify-between">{canDelete ? <Button variant="outline" onClick={handleDelete} disabled={deleting} className="border-red-200 text-red-700 hover:bg-red-50">{deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}Delete cheque</Button> : <span />}<div className="flex flex-col-reverse gap-2 sm:flex-row"><Button variant="outline" onClick={onClose}>Close</Button><Button variant="outline" onClick={handleDownload} disabled={loading || downloading}>{downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Download PDF</Button>{editable && <Button onClick={handleSave} disabled={loading || saving} className="bg-[#062B1A] text-white">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save</Button>}</div></DialogFooter>
    </DialogContent>
  </Dialog>;
}
